import type { MessageKey } from 'types/LocaleMessageKey';

import type { MESSAGE_SUCCESS_DEFAULT } from '@/app/[locale]/store/_consts/discounts';
import {
  ERROR_EXPIRED,
  ERROR_NO_USES_REMAIN,
  MESSAGE_ERROR_DEFAULT,
  MESSAGE_ERROR_EXPIRED,
  MESSAGE_ERROR_USED,
  MESSAGE_HIGHEST_DISCOUNT,
} from '@/app/[locale]/store/_consts/discounts';

import type { DiscountError, UiDiscount } from './types';

const _showHighestDiscountMessage = (
  { errorMessage, invalidReason }: DiscountError,
  promotionCodes: (string | null | undefined)[],
  campaign?: string | null | undefined,
) => {
  const hasError = errorMessage ?? invalidReason;

  if (hasError) return true;

  // show sitewide multiple discount message if there are multiple discount codes and one of them is sitewide
  return promotionCodes.length > 1 || campaign;
};

const _invalidMessages = (reason?: string) => {
  switch (reason) {
    case ERROR_NO_USES_REMAIN:
      return MESSAGE_ERROR_USED;

    case ERROR_EXPIRED:
      return MESSAGE_ERROR_EXPIRED;

    default:
      return MESSAGE_ERROR_DEFAULT;
  }
};

interface GetUrlDiscountMessage {
  campaign: string | null | undefined;
  campaignFound: UiDiscount | undefined;
  defaultMessage: typeof MESSAGE_SUCCESS_DEFAULT | null;
  discountError: DiscountError;
  discountFound: UiDiscount | undefined;
  errorCode: string | null | undefined;
  errorMessage: MessageKey | null;
  promotionCodes: (string | null | undefined)[];
  reason: string | null | undefined;
  showHighestDiscountEyebrow: boolean | undefined;
  showUrlDiscount: boolean;
}

const _getUrlDiscountMessage = ({
  campaign,
  campaignFound,
  defaultMessage,
  discountError,
  discountFound,
  errorCode,
  errorMessage,
  promotionCodes,
  reason,
  showHighestDiscountEyebrow,
  showUrlDiscount,
}: GetUrlDiscountMessage) => {
  if (showUrlDiscount) {
    // if has errorMessage, show it
    if (errorMessage) return errorMessage;
    // if has reason, show it
    if (reason) return _invalidMessages(reason);
    // if has errorCode, show it
    if (errorCode) return _invalidMessages();
    // if valid discount found, show custom statsig message
    if (discountFound) return discountFound.discountBanner;
    // if valid campaign found, show custom statsig message
    if (campaignFound) return campaignFound.discountBanner;
    // if has sitewide discount and url discount show highest discount message
    if (
      showHighestDiscountEyebrow &&
      _showHighestDiscountMessage(discountError, promotionCodes, campaign)
    ) {
      return MESSAGE_HIGHEST_DISCOUNT;
    }
    // if has applied discount code, show default discount message
    return defaultMessage;
  }

  return null;
};

export default _getUrlDiscountMessage;
